import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Store, Phone, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { CommunityNav } from "@/components/community/CommunityNav";
import { api } from "@/lib/api";

const SITE = "https://lagooncityrealestate.ca";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function LocalBusinesses() {
  const [businesses, setBusinesses] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>Local Businesses — Lagoon City Community | Joselle Alves</title>
        <meta name="description" content="Cafés, restaurants, the marina and trusted local businesses that make Lagoon City feel like home." />
        <link rel="canonical" href={`${SITE}/community/local-businesses`} />
      </Helmet>
      <PageHeader />
      <CommunityNav />

      <section className="relative overflow-hidden">
        <div className="absolute -left-32 top-10 w-[24rem] h-[24rem] rounded-full bg-[#E890A7]/7 blur-[110px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-10 sm:pt-20 sm:pb-12 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="rounded-3xl overflow-hidden border border-[#E890A7]/12">
            <img src="/assets/community/dockyard-interior.jpg" alt="Waterfront dining in Lagoon City" className="w-full h-72 sm:h-96 object-cover" />
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4 flex items-center gap-2"><Store size={14} /> Dining &amp; Local Life</p>
            <h1 data-testid="businesses-title" className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Cafés, restaurants and businesses that <em className="italic text-[#E890A7]">know your name</em>
            </h1>
            <p className="mt-5 text-slate-400 font-light leading-relaxed">
              The community is anchored by welcoming local spots — the vintage Phil &amp; Rosie's Coffee Shop across from
              the Yacht Club, Lanterna's fresh Italian takeaway on Lakeshore Drive, waterfront dining at the Dockyard
              Grill, and trusted trades and services nearby. It's small-town warmth with everything you need close by.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-center gap-3 mb-8">
          <Store size={18} className="text-[#E890A7]" />
          <h2 className="font-display text-2xl sm:text-3xl">Local businesses we love</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="business-directory">
          {businesses.map((b) => (
            <motion.div key={b.business_id} data-testid={`business-card-${slug(b.name)}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
              className="group rounded-3xl overflow-hidden border border-[#E890A7]/15 bg-[#151218]/70 hover:border-[#E890A7]/45 hover:-translate-y-1 transition-all duration-300 flex flex-col">
              {b.img ? (
                <div className={`h-44 overflow-hidden ${b.logo ? "bg-white flex items-center justify-center p-6" : ""}`}>
                  <img src={b.img} alt={b.name} className={`w-full h-full ${b.logo ? "object-contain group-hover:scale-105 transition-transform duration-500" : "object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"}`} />
                </div>
              ) : (
                <div className="h-24 bg-gradient-to-br from-[#E890A7]/15 to-transparent flex items-end p-5"><Store size={22} className="text-[#E890A7]/70" /></div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7]/70">{b.kind}</p>
                <p className="font-display text-2xl mt-2 leading-snug">{b.name}</p>
                <p className="text-sm text-slate-400 mt-2 font-light leading-relaxed flex-1">{b.blurb}</p>
                <div className="mt-5 flex items-center gap-4">
                  {b.phone && (
                    <a href={`tel:+1${b.phone.replace(/\D/g, "")}`} className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-[#F2A7BA] transition-colors"><Phone size={12} /> {b.phone}</a>
                  )}
                  {b.url && (
                    <a href={b.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-[#F2A7BA] hover:gap-2 transition-all duration-300">{b.url_label || "Visit"} <ArrowUpRight size={12} /></a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
          <button data-testid="business-feature-cta" onClick={() => { window.location.href = "/#contact"; }}
            className="rounded-3xl border border-dashed border-[#E890A7]/25 p-8 flex flex-col items-start justify-center text-left hover:border-[#E890A7]/60 hover:bg-[#E890A7]/5 transition-all duration-300 min-h-[16rem]">
            <p className="font-display italic text-2xl text-[#E890A7]/80">Your business here?</p>
            <p className="text-sm text-slate-400 mt-3 font-light">Own a local business in Lagoon City? Ask Joselle to feature it in this directory.</p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm text-[#F2A7BA]">Get featured <ArrowUpRight size={15} /></span>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
